import moment from 'moment-timezone';

export const getScheduleDateRange = (start, range) => {
    if (!start || !range) {
        // DEBUG
        // eslint-disable-next-line no-console
        console.warn('⚠️ Warning: getScheduleDateRange received invalid arguments');
        return {};
    }

    const startDate = start.clone().startOf('day');
    const endDate = range === 'day' ? start.clone().endOf(range) : start.clone().add(6, 'days').endOf('day');

    return { startDate, endDate };
};

export const idString = (id) => (typeof id === 'string' ? id : null);

export const getRowId = (row) => {
    return row._id?._id ?? idString(row._id) ?? 'unassigned';
};

export const getIdsArray = (selectedItems) => {
    return Object.keys(selectedItems).reduce((acc, key) => {
        acc.push(...Array.from(selectedItems[key]));
        return acc;
    }, []);
};

export const getItemGroup = (item, groupBy) => {
    if (!item || !groupBy) return null;
    if (['start_date', 'date'].includes(groupBy)) {
        return item[groupBy] ? moment(item[groupBy]).format('YYYY-MM-DD') : 'unassigned';
    }
    if (groupBy === 'processor_id') {
        return item.processor_id?._id ?? item.processor_id ?? 'unassigned';
    }
    if (groupBy === 'hauling_id') {
        return item.hauling_id?._id ?? item.hauling_id ?? 'unassigned';
    }
    return item[groupBy] ?? 'unassigned';
};

export const updateSupplyItemCacheRowById = async ({ queryClient, queryKeyTag, fetch, documentIds, order }) => {
    const data = await fetch();
    if (!data) {
        // DEBUG
        // eslint-disable-next-line no-console
        console.warn('⚠️ Warning: updateCacheRowById received no data from fetch');
        return;
    }

    const isDeleted = !data.length;
    const existingQueries = queryClient.getQueriesData({ queryKey: [queryKeyTag] });

    const newGroupIds = data.map((row) => getRowId(row));
    const newGroupIdsSet = new Set(newGroupIds);
    const queryKeysToInvalidate = [];
    existingQueries.forEach(([queryKey, queryData]) => {
        if (!queryData) return;

        // Check queryData for groups that don't exist current cache
        // If they don't exist, invalidate the query to refresh the data and return
        let refreshRequired = false;
        newGroupIdsSet.forEach((groupId) => {
            if (!queryData.some((row) => getRowId(row) === groupId)) {
                queryKeysToInvalidate.push(queryKey);
                refreshRequired = true;
            }
        });
        if (refreshRequired) return;

        const newData = queryData.map((row) => {
            if (!row.items) return row;

            let updatedItems = [];
            if (isDeleted) {
                updatedItems = row.items.filter((item) => !documentIds.includes(item?._id));
            } else {
                updatedItems = [...row.items];
                const rowId = getRowId(row);

                // Loop over data, check if rowId matches new groupId
                // if so, update or add item
                // if not, check if item exists in row.items
                // if so, remove it from updatedItems
                // if not, do nothing
                data.forEach((newRow) => {
                    if (!newRow?.items?.length) return;
                    const newItemGroupId = getRowId(newRow);
                    newRow.items.forEach((newItem) => {
                        let existingItemIndex = row.items.findIndex((i) => i._id === newItem._id);
                        // newItem belongs in this row, update or add it to new query data
                        if (newItemGroupId === rowId) {
                            if (order === 'updated_at DESC') {
                                // remove and insert at the start
                                updatedItems.splice(existingItemIndex, 1);
                                updatedItems.unshift({ ...newItem });
                            } else if (existingItemIndex > -1) {
                                updatedItems[existingItemIndex] = { ...newItem };
                            } else {
                                updatedItems.push(newItem);
                            }

                            if (order === 'start_date ASC') {
                                updatedItems.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
                            } else if (order === 'end_date ASC') {
                                updatedItems.sort((a, b) => new Date(a.end_date) - new Date(b.end_date));
                            }
                        } else if (existingItemIndex > -1) {
                            // newItem exists in old query data but is no longer in this row, remove it
                            updatedItems.splice(existingItemIndex, 1);
                        }
                    });
                });
            }
            return {
                ...row,
                items: updatedItems,
            };
        });

        queryClient.setQueryData(queryKey, newData);
    });

    queryKeysToInvalidate.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
    return { invalidatedQueryKeys: queryKeysToInvalidate };
};

export const updateDemandItemCacheRowById = async ({ queryClient, queryKeyTag, fetch, documentIds, groupBy }) => {
    const data = await fetch();
    if (!data) {
        // DEBUG
        // eslint-disable-next-line no-console
        console.warn('⚠️ Warning: updateCacheRowById received no data from fetch');
        return;
    }

    const isDeleted = !data.length;
    const existingQueries = queryClient.getQueriesData({ queryKey: [queryKeyTag] });

    const newGroupIds = data.map((item) => getItemGroup(item, groupBy));
    const newGroupIdsSet = new Set(newGroupIds);
    const queryKeysToInvalidate = [];
    existingQueries.forEach(([queryKey, queryData]) => {
        if (!queryData) return;

        // Check queryData for groups that don't exist current cache
        // If they don't exist, invalidate the query to refresh the data and return
        let refreshRequired = false;
        newGroupIdsSet.forEach((groupId) => {
            if (!queryData.some((row) => getRowId(row) === groupId)) {
                queryKeysToInvalidate.push(queryKey);
                refreshRequired = true;
            }
        });
        if (refreshRequired) return;

        const newData = queryData.map((row) => {
            if (!row.items) return row;

            let updatedItems = [];
            if (isDeleted) {
                updatedItems = row.items.filter((item) => !documentIds.includes(item?._id));
            } else {
                updatedItems = [...row.items];
                const rowId = getRowId(row);

                // Loop over data, check if rowId matches new groupId
                // if so, update or add item
                // if not, check if item exists in row.items
                // if so, remove it from updatedItems
                // if not, do nothing
                data.forEach((newItem) => {
                    const newItemGroupId = getItemGroup(newItem, groupBy);
                    let existingItemIndex = row.items.findIndex((i) => i._id === newItem._id);
                    // newItem belongs in this row, update or add it to new query data
                    if (newItemGroupId === rowId) {
                        if (existingItemIndex > -1) {
                            updatedItems[existingItemIndex] = { ...newItem };
                        } else {
                            updatedItems.push(newItem);
                        }
                    } else if (existingItemIndex > -1) {
                        // newItem exists in old query data but is no longer in this row, remove it
                        updatedItems.splice(existingItemIndex, 1);
                    }
                });
            }
            return {
                ...row,
                items: updatedItems,
            };
        });

        queryClient.setQueryData(queryKey, newData);
    });

    queryKeysToInvalidate.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
    return { invalidatedQueryKeys: queryKeysToInvalidate };
};

export const sortHaulersDashboard = (a, b) => {
    const aHasSupply = Object.values(a).some((v) => v && typeof v === 'object' && v.supplyCount > 0);
    const bHasSupply = Object.values(b).some((v) => v && typeof v === 'object' && v.supplyCount > 0);

    // First sort: items with supply go before items without supply
    if (aHasSupply !== bHasSupply) {
        return aHasSupply ? -1 : 1;
    }

    // Second sort: alphabetical by transport
    return a.transport.localeCompare(b.transport);
};

export const resetFilters = {
    order: 'start_date ASC',
    search: {
        'route_id.name': '',
    },
    processor_id: [],
    hauling_id: [],
    milk_category: [],
};

export const isItemMatched = (item) => {
    const { processor_id } = item;
    const matchedProcessor = item?.autopilot_metadata?.processor_match_id?._id;
    if (!matchedProcessor) return false;
    return processor_id?._id === matchedProcessor;
};

export const calculateProratedProducerPickupSequenceAmount = ({ amount, producer_pickup_sequence }) => {
    // preserve the original ratio of expected amounts and update with the new amount
    const itemAmount = amount ?? 0;
    const sequenceSumAmount = producer_pickup_sequence.reduce((acc, producer) => acc + (producer.expected_amount ?? 0), 0);
    const adjustedSequence = producer_pickup_sequence.map((producer) => {
        if (itemAmount === 0 || sequenceSumAmount === 0) {
            return producer;
        }
        const expected_amount = Math.floor((producer.expected_amount / sequenceSumAmount) * itemAmount);
        return {
            ...producer,
            expected_amount,
        };
    });
    const adjustedSequenceSum = adjustedSequence.reduce((acc, producer) => acc + (producer.expected_amount ?? 0), 0);

    // Since we are rounding down all the expected amounts, we may end up with a smaller total than the item amount.
    // We need to adjust the last producer's expected amount to make up the difference.
    const remainder = itemAmount - adjustedSequenceSum;
    const lastProducerIndex = adjustedSequence.length - 1;
    if (remainder > 0) {
        adjustedSequence[lastProducerIndex].expected_amount += remainder;
    }
    return adjustedSequence;
};

export const convertStringToNumber = (value) => {
    if (value === '' || !value) return 0;
    return Number(value);
};

export const reorderColumn = ({ field, oldIndex, targetIndex, currentOrder, allColumns }) => {
    const baseOrder = currentOrder?.length ? [...currentOrder] : allColumns.map((col) => col.field);

    if (oldIndex < 0 || targetIndex < 0) return baseOrder;

    const filtered = baseOrder.filter((f) => f !== field);
    filtered.splice(targetIndex, 0, field);

    return filtered;
};

export const sanitizeVisibilityModel = (savedModel = {}, columns) => {
    return Object.fromEntries(columns.map((col) => [col.field, savedModel[col.field] ?? true]));
};

export const MilkClassification = Object.freeze({
    A2: 'A2',
    Kosher: 'Kosher',
    PP: 'PP',
});
